// DS EDIT 9 — deployment-freshness helpers (no supabase, no DOM).

export interface DeploymentMeta {
  buildId: string;
  deployedAt?: string;
  version?: string;
  summary?: string;
}

export const SUMMARY_MAX_CHARS = 400;
export const BACKOFF_INITIAL_MS = 5_000;
export const BACKOFF_MAX_MS = 5 * 60_000;

/**
 * Runtime validation of /deployment-meta.json. Malformed or incomplete
 * responses return null and are ignored (never surfaced to the user).
 * summary is coerced to bounded plain text (tags stripped) before use.
 */
export function parseDeploymentMeta(raw: unknown): DeploymentMeta | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.buildId !== "string") return null;
  const buildId = r.buildId.trim();
  if (!buildId || buildId.length > 128) return null;
  const meta: DeploymentMeta = { buildId };
  if (typeof r.deployedAt === "string" && r.deployedAt.length <= 64) meta.deployedAt = r.deployedAt;
  if (typeof r.version === "string" && r.version.trim() && r.version.length <= 64) {
    meta.version = r.version.trim();
  }
  if (typeof r.summary === "string") {
    const plain = r.summary.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (plain) meta.summary = plain.slice(0, SUMMARY_MAX_CHARS);
  }
  return meta;
}

/**
 * Opaque inequality comparison — no semantic-version ordering assumed.
 * Dev/local builds never prompt (their ids are not deployment ids).
 */
export function isNewerBuild(
  currentBuildId: string | null | undefined,
  latestBuildId: string | null | undefined
): boolean {
  const current = (currentBuildId || "").trim();
  const latest = (latestBuildId || "").trim();
  if (!current || !latest) return false;
  if (current === "dev" || current.startsWith("local-")) return false;
  return current !== latest;
}

/**
 * Bounded exponential backoff with jitter: ~5s, ~10s, ~20s ... capped at 5min.
 * rand01 is injected for deterministic tests; jitter spreads ±20%.
 */
export function computeBackoffMs(consecutiveFailures: number, rand01 = 0.5): number {
  const n = Math.max(1, Math.floor(consecutiveFailures));
  const base = Math.min(BACKOFF_INITIAL_MS * 2 ** (n - 1), BACKOFF_MAX_MS);
  const clamped = Math.min(Math.max(rand01, 0), 1);
  return Math.round(base * (0.8 + 0.4 * clamped));
}
